import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  QUERY_KEY_GET_MY_MONITORING_ACCOUNT,
  QUERY_KEY_GET_MY_MONITORING_ACCOUNT_UNITS,
  useGetMyMonitoringAccount,
  useUpdateMyMonitoringAccount
} from '@apps-common/apis/portal/my-monitoring'
import { ButtonRoundedSm, InputSM, SelectInputSM } from '@apps-common/components/elements'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@apps-common/config/regex'
import { MY_MONITORING_ROLES_OPTIONS } from '@apps-common/config/user-roles'
import { queryClient } from '@apps-common/lib'
import { IMyMonitoringAccountModel } from '@apps-common/types/portal/my-monitoring'

export const MyMonitoringUserModifyModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { userId: number }
}) => {
  const { userId } = componentProps

  const afterChange = (role: string) => {
    if (role === ModalDismiss.Confirm) {
      queryClient.refetchQueries({ queryKey: QUERY_KEY_GET_MY_MONITORING_ACCOUNT(userId) })
      queryClient.refetchQueries({ queryKey: QUERY_KEY_GET_MY_MONITORING_ACCOUNT_UNITS(userId) })
    }
    onDismiss(null, role)
  }

  const {
    isLoading: accountIsLoading,
    isError: accountIsError,
    isFetching: accountIsFetching,
    isRefetching: accountIsRefetching,
    data: accountData
  } = useGetMyMonitoringAccount({ id: userId })

  const { mutate: updateAccount, isPending: updateAccountIsPending } = useUpdateMyMonitoringAccount(true, (result: boolean) => {
    if (result) {
      afterChange(ModalDismiss.Confirm)
    }
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset
  } = useForm<IMyMonitoringAccountModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (accountData) {
      reset(accountData)
    }
  }, [accountData])

  const submit = (values: IMyMonitoringAccountModel) => updateAccount({ model: values })

  const isLoading = accountIsLoading || accountIsError || accountIsFetching || accountIsRefetching
  const isUpdating = isSubmitting || updateAccountIsPending

  return (
    <ModalContainer innerClasses='sm:!min-h-0 !h-auto'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Update' title='User details' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px]'>
        <form id='my-monitoring-user-details-update-form' noValidate onSubmit={handleSubmit(submit)}>
          <InputSM
            key='email'
            type='text'
            placeholder='User account email'
            label='Email'
            id='email'
            showLabel
            loading={isLoading}
            disabled={isUpdating || accountData?.migrated}
            info='Changing email after account created/migrated is not allowed.'
            registration={register('email', {
              required: FORM_DEFAULT_REQUIRED,
              pattern: EMAIL_VALIDATION_REGEX
            })}
            errors={errors.email}
          />
          <InputSM
            key='firstName'
            type='text'
            placeholder='User first name'
            label='First name'
            id='firstName'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('firstName', {
              required: FORM_DEFAULT_REQUIRED
            })}
            errors={errors.firstName}
          />
          <InputSM
            key='lastName'
            type='text'
            placeholder='User last name'
            label='Last name'
            id='lastName'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('lastName', {
              required: FORM_DEFAULT_REQUIRED
            })}
            errors={errors.lastName}
          />
          <InputSM
            key='phoneNumber'
            type='text'
            placeholder='User phone number'
            label='Phone number'
            id='phoneNumber'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('phoneNumber', {
              required: false,
              pattern: PHONE_VALIDATION_REGEX
            })}
            errors={errors.phoneNumber}
          />
          <SelectInputSM
            type='text'
            label='Role'
            id='role'
            showLabel
            className='capitalize'
            loading={isLoading}
            disabled={isUpdating}
            registration={register('role', {
              required: false
            })}
            options={MY_MONITORING_ROLES_OPTIONS}
            errors={errors.role}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <ButtonRoundedSm
          type='submit'
          form='my-monitoring-user-details-update-form'
          loading={isLoading || isUpdating}
          text='Update'
          className='action-success'
        />
      </ModalFooter>
    </ModalContainer>
  )
}
